import { PropsWithChildren, useRef } from 'react';
import styles from './WidgetContainer.module.scss';
import cn from 'classnames';
import { handleActions, handleItemFocus } from '@chewy/chat-library-common';

export interface WidgetContainerProps {
  className?: string;
  /** When set to chatwrapupcard (case-insensitive), container uses a transparent background. */
  widgetType?: string;
  isSelectable?: boolean;
  /** Whether the card contains a CTA or action link and should use emphasized styling. */
  hasActions?: boolean;
  isSelected?: boolean;
  isUnselected?: boolean;
  onSelect?: () => void;
  data?: Record<string, any>;
  containerStyle?: React.CSSProperties;
}

export default function WidgetContainer({
  children,
  className,
  widgetType,
  isSelectable = false,
  hasActions = false,
  isSelected = false,
  isUnselected = false,
  onSelect,
  data,
  containerStyle = {},
}: PropsWithChildren<WidgetContainerProps>) {
  const containerRef = useRef<HTMLDivElement>(null);

  const performAction = (e: React.KeyboardEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    const target = e.target;

    if (!(target instanceof HTMLElement)) {
      return;
    }

    // Get the nearest interactive ancestor
    const interactiveElement = target.closest(
      'button:not([data-testid="widget-overlay-button"]), a, input, textarea, select, [data-kib-truncation-toggle]'
    );

    // If the nearest interactive ancestor is not the Widget Container itself,
    // let it handle the event
    if (interactiveElement && interactiveElement !== containerRef.current) {
      return;
    }

    e.preventDefault();

    if (isSelectable && !isSelected && !isUnselected) {
      if (onSelect) {
        onSelect();
      }
      if (data?.defaultAction) {
        handleActions(data.defaultAction, data);
      }
      if (containerRef.current) {
        try {
          handleItemFocus(containerRef.current, data);
        } catch (error) {
          console.error('Error handling item focus:', error);
        }
      }
    }
  }

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    performAction(e);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' || e.key === ' ') {
      performAction(e);
    }
  };

  const isChatWrapupCard = widgetType?.toLowerCase() === 'chatwrapupcard';

  return (
    <div
      ref={containerRef}
      className={cn(styles.widgetContainer, className, {
        [styles.selectable]: isSelectable,
        [styles.actionCard]: hasActions,
        [styles.selected]: isSelected,
        [styles.unselected]: isUnselected,
        [styles.chatWrapupCard]: isChatWrapupCard,
      })}
      style={containerStyle}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onTouchStart={() => { }} // enables CSS :active on iOS Safari
    >
      <div className={cn(styles.widgetContent)}>
        {children}
      </div>
    </div>
  );
}
